import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Product } from '@/types';
import { calcDiscountPct } from '@/utils/helpers';
import { useCart } from '@/context/CartContext';

interface Props {
  product: Product;
  onAddToCart?: (product: Product) => void;
  variant?: 'vertical' | 'snap' | 'aliexpress';
}

const FALLBACK_IMG =
  'https://readdy.ai/api/search-image?query=solar%20panel%20renewable%20energy%20product%20clean%20white%20background%20professional%20ecommerce%20photography%20minimal&width=400&height=400&seq=card-fallback-001&orientation=squarish';

export default function ProductCard({ product, onAddToCart, variant = 'vertical' }: Props) {
  const [cartState, setCartState] = useState<'idle' | 'adding' | 'added'>('idle');
  const [imgIndex, setImgIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { addItem, openCart } = useCart();

  const imgCandidates = [...(product.imageCandidates ?? [product.thumbnail]), FALLBACK_IMG];
  const currentImgSrc = imgCandidates[Math.min(imgIndex, imgCandidates.length - 1)];
  const handleImgError = () => setImgIndex((prev) => Math.min(prev + 1, imgCandidates.length - 1));

  const discount = product.originalPrice
    ? calcDiscountPct(product.originalPrice, product.price)
    : (product.discount ?? 0);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product.inStock || cartState !== 'idle') return;
    setCartState('adding');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setCartState('added');
      addItem(product, 1);
      openCart();
      onAddToCart?.(product);
      timerRef.current = setTimeout(() => setCartState('idle'), 2000);
    }, 400);
  };

  /* ─────────────────────────────────────────────
     SNAP VARIANT — mobile immersive card
  ───────────────────────────────────────────── */
  if (variant === 'snap') {
    return (
      <Link
        to={`/products/${product.id}`}
        className="group relative flex flex-col rounded-3xl overflow-hidden cursor-pointer active:scale-[0.97] transition-transform duration-150"
        style={{ aspectRatio: '3/4' }}
      >
        <img src={currentImgSrc} alt={product.name} loading="lazy" decoding="async" onError={handleImgError}
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute top-0 inset-x-0 flex items-start justify-between p-2.5 z-20">
          {discount > 0
            ? <span className="text-[10px] font-black px-2 py-1 rounded-full bg-rose-500 text-white">-{discount}%</span>
            : <span />}
          {!product.inStock && (
            <span className="text-[9px] font-bold text-white bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">Sin stock</span>
          )}
        </div>
        <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col justify-end px-3 pb-3 pt-12"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.45) 55%, transparent 100%)' }}>
          <h3 className="font-bold text-[13px] leading-snug text-white line-clamp-2 mb-2">{product.name}</h3>
          <div className="flex items-end justify-between gap-2 mb-2.5">
            <span className="font-black text-[16px] leading-none text-white">${product.price.toFixed(2)}</span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-[10px] text-white/40 line-through self-end">${product.originalPrice.toFixed(2)}</span>
            )}
          </div>
          <button onClick={handleAddToCart} disabled={!product.inStock || cartState === 'adding'}
            className={`w-full flex items-center justify-center gap-1.5 py-2.5 rounded-2xl text-[12px] font-bold transition-all duration-200 cursor-pointer whitespace-nowrap min-h-[40px] ${
              cartState === 'added' ? 'bg-emerald-500 text-white' :
              cartState === 'adding' ? 'bg-white/20 text-white backdrop-blur-sm' :
              product.inStock ? 'bg-white text-neutral-900 active:scale-95' :
              'bg-white/20 text-white/40 cursor-not-allowed'
            }`}>
            {cartState === 'added' ? <><i className="ri-check-line" />Agregado</> :
             cartState === 'adding' ? <><i className="ri-loader-4-line animate-spin" />Agregando...</> :
             <><i className="ri-shopping-bag-line" />Agregar</>}
          </button>
        </div>
      </Link>
    );
  }

  /* ─────────────────────────────────────────────
     ALIEXPRESS VARIANT — mobile grid card
  ───────────────────────────────────────────── */
  if (variant === 'aliexpress') {
    return (
      <Link
        to={`/products/${product.id}`}
        className="flex flex-col overflow-hidden cursor-pointer h-full active:scale-[0.98] transition-transform duration-150"
        style={{
          borderRadius: 14,
          background: '#ffffff',
          border: '1px solid #ede8e0',
        }}
      >
        {/* Image */}
        <div className="relative overflow-hidden shrink-0" style={{ aspectRatio: '1/1', background: '#f5f0ea' }}>
          {discount > 0 && (
            <span
              className="absolute top-2 left-2 z-10 text-[10px] font-bold px-2 py-0.5 rounded-md text-white"
              style={{ background: '#4bbfdf' }}
            >
              -{discount}%
            </span>
          )}
          {!product.inStock && (
            <div className="absolute inset-0 z-10 flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.70)' }}>
              <span className="text-[10px] font-medium text-stone-500 bg-white border border-stone-200 px-2 py-0.5 rounded-full">Sin stock</span>
            </div>
          )}
          <img
            src={currentImgSrc}
            alt={product.name}
            loading="lazy" decoding="async"
            onError={handleImgError}
            className="w-full h-full object-contain p-3 transition-transform duration-500 group-active:scale-105"
          />
        </div>

        {/* Info */}
        <div className="flex flex-col flex-1 px-3 pt-2.5 pb-3">
          <h3 className="text-[12px] font-medium text-stone-800 leading-snug line-clamp-2 flex-1" style={{ minHeight: '2.5em' }}>
            {product.name}
          </h3>

          <div className="mt-2.5 flex items-center justify-between gap-2">
            <div className="flex flex-col min-w-0">
              {product.originalPrice && product.originalPrice > product.price && (
                <span className="text-[10px] text-stone-400 line-through leading-none mb-0.5">
                  ${product.originalPrice.toFixed(2)}
                </span>
              )}
              <span className="font-bold text-[15px] leading-none text-stone-900" style={{ letterSpacing: '-0.02em' }}>
                ${product.price.toFixed(2)}
              </span>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={!product.inStock || cartState === 'adding'}
              aria-label="Agregar al carrito"
              className="shrink-0 flex items-center justify-center cursor-pointer whitespace-nowrap transition-all duration-200"
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                fontSize: 14,
                border: 'none',
                background:
                  cartState === 'added' ? '#10b981' :
                  cartState === 'adding' ? '#e8f8fd' :
                  product.inStock ? '#4bbfdf' : '#f0f9fc',
                color:
                  cartState === 'added' ? '#fff' :
                  cartState === 'adding' ? '#7dd3e8' :
                  product.inStock ? '#fff' : '#a8a29e',
              }}
            >
              {cartState === 'added' ? <i className="ri-check-line" /> :
               cartState === 'adding' ? <i className="ri-loader-4-line animate-spin" /> :
               <i className="ri-shopping-cart-line" />}
            </button>
          </div>
        </div>
      </Link>
    );
  }

  /* ─────────────────────────────────────────────
     VERTICAL VARIANT — desktop premium card
     Fixed heights: every card is exactly 420px tall
  ───────────────────────────────────────────── */
  return (
    <Link
      to={`/products/${product.id}`}
      className="relative flex flex-col overflow-hidden cursor-pointer group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        height: 378,
        borderRadius: 16,
        background: '#ffffff',
        border: hovered ? '1.5px solid #4bbfdf' : '1.5px solid #ede8e0',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1), border-color 0.2s ease',
      }}
    >
      {/* ── IMAGE ── */}
      <div
        className="relative overflow-hidden"
        style={{ height: 220, minHeight: 220, maxHeight: 220, background: '#f5f0ea' }}
      >
        {/* Discount pill */}
        {discount > 0 && (
          <span
            className="absolute top-2.5 left-2.5 z-20 text-[10px] font-bold px-2 py-0.5 rounded-md text-white"
            style={{ background: '#4bbfdf' }}
          >
            -{discount}%
          </span>
        )}

        {/* Badge */}
        {product.badge && !discount && (
          <span
            className="absolute top-2.5 right-2.5 z-20 text-[10px] font-semibold px-2 py-0.5 rounded-md text-stone-600"
            style={{ background: '#ede8e0', border: '1px solid #d4c9bc' }}
          >
            {product.badge}
          </span>
        )}

        {/* Out of stock */}
        {!product.inStock && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.72)' }}
          >
            <span className="text-[11px] font-medium text-stone-500 bg-white border border-stone-200 px-3 py-1.5 rounded-full">
              Sin stock
            </span>
          </div>
        )}

        {/* Product image */}
        <img
          src={currentImgSrc}
          alt={product.name}
          loading="lazy" decoding="async"
          width="220"
          height="220"
          onError={handleImgError}
          className="w-full h-full object-contain p-5"
          style={{
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1)',
          }}
        />

        {/* Specs pills on hover */}
        {(product.power || product.warranty) && (
          <div
            className="absolute bottom-2.5 inset-x-2.5 z-20 flex gap-1.5 flex-wrap justify-center"
            style={{
              opacity: hovered ? 1 : 0,
              transform: hovered ? 'translateY(0)' : 'translateY(4px)',
              transition: 'opacity 0.22s ease, transform 0.22s ease',
            }}
          >
            {product.power && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full text-stone-700"
                style={{ background: 'rgba(255,255,255,0.95)', border: '1px solid #e8e2d9' }}
              >
                <i className="ri-flashlight-line" style={{ color: '#4bbfdf' }} />
                {product.power}
              </span>
            )}
            {product.warranty && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full text-stone-700"
                style={{ background: 'rgba(255,255,255,0.95)', border: '1px solid #e8e2d9' }}
              >
                <i className="ri-shield-check-line" style={{ color: '#10b981' }} />
                {product.warranty}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── INFO ── */}
      <div
        className="flex flex-col px-3.5 pt-2.5 pb-2.5"
        style={{ height: 158, minHeight: 158, maxHeight: 158 }}
      >
        {/* Category */}
        <span
          className="text-[10px] font-semibold uppercase tracking-widest text-stone-400 truncate"
          style={{ height: 14, lineHeight: '14px', marginBottom: 3 }}
        >
          {product.categoryId.replace(/-/g, ' ')}
        </span>

        {/* Name — exactly 2 lines, always */}
        <h3
          className="font-semibold text-[13px] text-stone-800 line-clamp-2"
          style={{
            letterSpacing: '-0.01em',
            height: 36,
            minHeight: 36,
            maxHeight: 36,
            lineHeight: '18px',
            overflow: 'hidden',
          }}
        >
          {product.name}
        </h3>

        {/* Thin divider */}
        <div
          className="h-px"
          style={{ background: '#f0ebe4', marginTop: 6, marginBottom: 6 }}
        />

        {/* Price + CTA row — flush to bottom */}
        <div className="flex items-end justify-between gap-2 flex-1">
          {/* Price block */}
          <div className="flex flex-col min-w-0 justify-end">
            {product.originalPrice && product.originalPrice > product.price ? (
              <span className="text-[10px] text-stone-400 line-through leading-none mb-0.5">
                ${product.originalPrice.toFixed(2)}
              </span>
            ) : (
              <span className="leading-none mb-0.5" style={{ height: 12 }} />
            )}
            <div className="flex items-baseline gap-1">
              <span
                className="font-bold text-[15px] text-stone-900 leading-none"
                style={{ letterSpacing: '-0.02em' }}
              >
                ${product.price.toFixed(2)}
              </span>
              <span className="text-[9px] text-stone-400 font-medium leading-none">
                USD
              </span>
            </div>
          </div>

          {/* Cart button — circular */}
          <button
            onClick={handleAddToCart}
            disabled={!product.inStock || cartState === 'adding'}
            aria-label="Agregar al carrito"
            className="shrink-0 flex items-center justify-center cursor-pointer whitespace-nowrap transition-all duration-200"
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              fontSize: 13,
              background:
                cartState === 'added' ? '#10b981' :
                hovered && product.inStock && cartState === 'idle' ? '#4bbfdf' :
                cartState === 'adding' ? '#e8f8fd' :
                product.inStock ? '#e8f8fd' : '#f0f0f0',
              color:
                cartState === 'added' ? '#fff' :
                hovered && product.inStock && cartState === 'idle' ? '#fff' :
                cartState === 'adding' ? '#7dd3e8' :
                product.inStock ? '#4bbfdf' : '#c4b9ae',
              border: 'none',
            }}
          >
            {cartState === 'added' ? <i className="ri-check-line" /> :
             cartState === 'adding' ? <i className="ri-loader-4-line animate-spin" /> :
             <i className="ri-shopping-cart-line" />}
          </button>
        </div>
      </div>
    </Link>
  );
}